import React, { PropsWithChildren, useContext } from 'react'

import { Features } from 'generated/featureDefinitions'
import { MyExpensesRootPageQuery } from 'generated/resources/graphql'

interface MyExpensesProviderProps {
  data: MyExpensesRootPageQuery
}

interface MyExpensesContextType extends MyExpensesProviderProps {
  featureAvailabilities: {
    expensableGroups: boolean
    reimburseThroughPayroll: boolean
    reimburseThroughSepa: boolean
    taxAgencyHomologation: boolean
    reportOnBehalf: boolean
    canUseSpendingPolicies: boolean
    canUsePerDiems: boolean
    canCreateExpenses: boolean
    projectsManagement: boolean
    hasWorkflowSpendingPolicies: boolean
    dataCollectionEnabled: boolean
  }
}

const MyExpensesContext = React.createContext<MyExpensesContextType | null>(null)

export const MyExpensesContextProvider: React.FC<PropsWithChildren<MyExpensesProviderProps>> = ({
  data,
  children,
}) => {
  const canUseExpensableGroups =
    data?.expenses?.expensableGroupsSettingsConnection?.nodes[0]?.enabled ?? false

  const canReimburseThroughPayroll = false

  const canCreateExpenses = data?.expenses?._policies?.expenseCreateExpenses?.can

  const canReimburseThroughSepa = false

  const canTaxAgencyHomologation =
    data.companies.featureAvailabilities.find((f) => f.id === Features.expenses_homologation)
      ?.available ?? false

  const canReportOnBehalf =
    data.companies.featureAvailabilities.find((f) => f.id === Features.expenses_report_on_behalf)
      ?.available ?? false

  const canUseSpendingPolicies =
    data.companies.featureAvailabilities.find((f) => f.id === Features.expenses_spending_policies)
      ?.available ?? false

  const canUsePerDiems =
    data.companies.featureAvailabilities.find((f) => f.id === Features.expenses_per_diems)
      ?.available ?? false

  const hasWorkflowSpendingPolicies = data.features.featuresConnection.nodes.some(
    (el) => el.name === 'dev_expenses_policies_with_workflows' && el.enabled
  )

  const projectsManagement =
    data.companies.featureAvailabilities.find((f) => f.id === Features.projects_management)
      ?.available ?? false

  const dataCollectionEnabled =
    data.features.featuresConnection.nodes.some(
      (el) => el.name === 'dev_expenses_data_collection' && el.enabled
    ) ?? false

  return (
    <MyExpensesContext.Provider
      value={{
        data,
        featureAvailabilities: {
          expensableGroups: canUseExpensableGroups,
          reimburseThroughPayroll: canReimburseThroughPayroll,
          reimburseThroughSepa: canReimburseThroughSepa,
          taxAgencyHomologation: canTaxAgencyHomologation,
          reportOnBehalf: canReportOnBehalf,
          canUseSpendingPolicies: canUseSpendingPolicies,
          canUsePerDiems: canUsePerDiems,
          canCreateExpenses: canCreateExpenses,
          projectsManagement: projectsManagement,
          hasWorkflowSpendingPolicies: hasWorkflowSpendingPolicies,
          dataCollectionEnabled: dataCollectionEnabled,
        },
      }}
    >
      {children}
    </MyExpensesContext.Provider>
  )
}

export const useMyExpenses = () => {
  const context = useContext(MyExpensesContext)

  if (!context) {
    throw new Error('useMyExpenses used without a provider')
  }

  return context
}
